import { RefObject, useEffect } from 'react'

const waitForPageLoad = () => {
  if (document.readyState === 'complete') return Promise.resolve()
  return new Promise<void>((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true })
  })
}

const IMAGES_LOAD_TIMEOUT_MS = 4000

/**
 * Waits for currently-loading images to finish, capped by a timeout so
 * off-screen `loading="lazy"` images (which the browser won't fetch until
 * they enter the viewport) can never block this indefinitely.
 */
const waitForImagesLoaded = () => {
  const pending = Array.from(document.images).filter((img) => !img.complete)
  if (pending.length === 0) return Promise.resolve()

  const allLoaded = Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true })
          img.addEventListener('error', () => resolve(), { once: true })
        })
    )
  )

  const timeout = new Promise<void>((resolve) =>
    setTimeout(resolve, IMAGES_LOAD_TIMEOUT_MS)
  )

  return Promise.race([allLoaded, timeout])
}

/**
 * Scroll `ref` into view once matching `hash` is present, only after the page
 * has fully loaded and all its images have finished loading — avoids
 * scrolling mid-layout-shift on client-side navigations, where
 * `document.readyState` is already 'complete' and `window.load` never fires again.
 */
export const useScrollToHashSection = (
  ref: RefObject<HTMLElement | null>,
  hash: string,
  ready: boolean
) => {
  useEffect(() => {
    if (window.location.hash !== hash) return
    if (!ready) return

    let cancelled = false

    const run = async () => {
      await waitForPageLoad()
      await waitForImagesLoaded()
      if (!cancelled) {
        ref.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [ready, hash, ref])
}
